import React, { useState, useEffect, useRef } from 'react';
import { auth } from '../../../../../../firebase/config';
import Modal from '../../../../../../components/Modal';
import TextField from '../../../../../../components/TextField';
import Button from '../../../../../../components/Button';
import { FiSearch, FiAlertTriangle } from 'react-icons/fi';
import Icon from '../../../../../../components/Icon';
import { submitChangeOfVendor, submitResubmitChangeOfVendor, fetchAvailableBranches, fetchAvailableCompanies, fetchDeployedCompaniesForBranch, fetchCompaniesByType, cancelChangeOfVendorRequest, type DeployedCompanyWithRequest } from '../service/addChangeOfVendorService';
import type { Branch, ChangeOfVendor } from '../../../service/changeOfVendorService';
import type { Company } from '../../../../../companies/service/companiesService';
import Toast from '../../../../../../components/Toast';
import { Markdown, MarkdownEditor } from '../../../../../../components/Markdown';

type AddChangeOfVendorModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    resubmitRequest?: ChangeOfVendor | null;
};

function AddChangeOfVendorModal({ isOpen, onClose, onSuccess, resubmitRequest }: AddChangeOfVendorModalProps) {
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
    const [selectedCompanyType, setSelectedCompanyType] = useState<string | null>(null);
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [availableBranches, setAvailableBranches] = useState<Branch[]>([]);
    const [deployedCompanies, setDeployedCompanies] = useState<DeployedCompanyWithRequest[]>([]);
    const [availableCompanies, setAvailableCompanies] = useState<Company[]>([]);
    const [requestDate, setRequestDate] = useState<string>('');
    const [deploymentDate, setDeploymentDate] = useState<string>('');
    
    // New fields state
    const [reason, setReason] = useState('');
    const [requestorName, setRequestorName] = useState('');
    const [requestorDesignation, setRequestorDesignation] = useState('');
    const [complaintStartDate, setComplaintStartDate] = useState('');
    const [followUpDates, setFollowUpDates] = useState<string[]>([]);
    const [newFollowUpDate, setNewFollowUpDate] = useState('');
    const [resubmissionNote, setResubmissionNote] = useState('');
    
    // Modal states
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictData, setConflictData] = useState<{ existingCompany: Company } | null>(null);
    
    // Search states
    const [branchSearchTerm, setBranchSearchTerm] = useState('');
    const [companySearchTerm, setCompanySearchTerm] = useState('');
    
    // UI states
    const [showToast, setShowToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingDeployments, setLoadingDeployments] = useState(false);
    const [saving, setSaving] = useState(false);
    const isSubmittingRef = useRef(false);

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';

    const showToastMessage = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setSuccessMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    // Initialize request date and deployment date when modal opens
    useEffect(() => {
        if (isOpen) {
            if (resubmitRequest) {
                // Pre-populate fields from the request to resubmit
                setSelectedBranch(resubmitRequest.branch || null);
                setSelectedCompanyType(resubmitRequest.company?.companyType || null);
                setSelectedCompany(resubmitRequest.company || null);
                setReason(resubmitRequest.reason || '');
                setRequestorName(resubmitRequest.requestorName || '');
                setRequestorDesignation(resubmitRequest.requestorDesignation || '');
                setComplaintStartDate(resubmitRequest.complaintStartDate || '');
                setFollowUpDates(resubmitRequest.followUpDates || []);
                
                const formatInputDate = (date: any) => {
                    if (!date) return '';
                    const dateObj = date.toDate ? date.toDate() : new Date(date);
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };

                setRequestDate(formatInputDate(resubmitRequest.requestDate));
                setDeploymentDate(formatInputDate(resubmitRequest.deploymentDate));
                setResubmissionNote('');
            } else {
                const today = new Date();
                const sevenDaysLater = new Date(today);
                sevenDaysLater.setDate(today.getDate() + 7);
                
                // Format dates as YYYY-MM-DD for input type="date"
                const formatDate = (date: Date) => {
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                };
                
                setRequestDate(formatDate(today));
                setDeploymentDate(formatDate(sevenDaysLater));
                setReason('');
                setRequestorName('');
                setRequestorDesignation('');
                setComplaintStartDate('');
                setFollowUpDates([]);
                setResubmissionNote('');
            }
        } else {
            // Reset when modal closes
            setRequestDate('');
            setDeploymentDate('');
            setSelectedBranch(null);
            setSelectedCompanyType(null);
            setSelectedCompany(null);
            setReason('');
            setRequestorName('');
            setRequestorDesignation('');
            setComplaintStartDate('');
            setFollowUpDates([]);
            setResubmissionNote('');
        }
    }, [isOpen, resubmitRequest]);

    const refreshDeployedCompanies = async () => {
        if (selectedBranch && uid) {
            setLoadingDeployments(true);
            try {
                const deployed = await fetchDeployedCompaniesForBranch(uid, isBankUser, selectedBranch.id);
                setDeployedCompanies(deployed);
            } catch (err) {
                console.error('Error fetching deployed companies:', err);
                showToastMessage('Failed to load deployed companies', 'error');
            } finally {
                setLoadingDeployments(false);
            }
        }
    };

    // Fetch branches when modal opens
    useEffect(() => {
        if (isOpen && uid) {
            const fetchData = async () => {
                setLoading(true);
                try {
                    const branches = await fetchAvailableBranches(uid, isBankUser);
                    setAvailableBranches(branches);
                } catch (err) {
                    console.error('Error fetching branches:', err);
                    showToastMessage('Failed to load branches', 'error');
                } finally {
                    setLoading(false);
                }
            };
            fetchData();
        }
    }, [isOpen, uid, isBankUser]);

    // Fetch deployed companies when branch is selected
    useEffect(() => {
        if (selectedBranch && uid) {
            refreshDeployedCompanies();
        } else {
            setDeployedCompanies([]);
            setSelectedCompanyType(null);
            setSelectedCompany(null);
        }
    }, [selectedBranch, uid, isBankUser]);

    const handleCancelRequest = async (requestDocId: string, requestId: string) => {
        if (!uid || !requestDocId) return;
        
        if (!window.confirm(`Are you sure you want to cancel request ${requestId}?`)) {
            return;
        }

        setLoadingDeployments(true);
        try {
            await cancelChangeOfVendorRequest(uid, isBankUser, requestDocId);
            showToastMessage(`✅ Request ${requestId} cancelled successfully`, 'success');
            // Refresh deployed companies to update the UI
            await refreshDeployedCompanies();
        } catch (err: any) {
            console.error('Error cancelling request:', err);
            showToastMessage(`❌ Failed to cancel request: ${err.message || 'Unknown error'}`, 'error');
        } finally {
            setLoadingDeployments(false);
        }
    };

    // Fetch companies by type when company type is selected
    useEffect(() => {
        if (selectedCompanyType && uid) {
            const fetchCompanies = async () => {
                setLoading(true);
                try {
                    const companies = await fetchCompaniesByType(uid, isBankUser, selectedCompanyType);
                    setAvailableCompanies(companies);
                } catch (err) {
                    console.error('Error fetching companies:', err);
                    showToastMessage('Failed to load companies', 'error');
                } finally {
                    setLoading(false);
                }
            };
            fetchCompanies();
        } else {
            setAvailableCompanies([]);
            setSelectedCompany(null);
        }
    }, [selectedCompanyType, uid, isBankUser]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedBranch(null);
            setSelectedCompanyType(null);
            setSelectedCompany(null);
            setDeployedCompanies([]);
            setAvailableCompanies([]);
            setBranchSearchTerm('');
            setCompanySearchTerm('');
            setShowBranchModal(false);
            setShowCompanyModal(false);
            setShowPreviewModal(false);
            setShowConflictModal(false);
            setConflictData(null);
            setSaving(false);
            isSubmittingRef.current = false;
        }
    }, [isOpen]);

    // Filter branches
    const filteredBranches = availableBranches.filter(branch => {
        const address = branch.address || '';
        const branchCode = branch.branchCode || '';
        const searchTerm = branchSearchTerm.toLowerCase();
        return address.toLowerCase().includes(searchTerm) || branchCode.toLowerCase().includes(searchTerm);
    });

    // Filter companies
    const filteredCompanies = availableCompanies.filter(company => {
        const companyName = company.companyName || '';
        const companyId = company.companyId || '';
        const searchTerm = companySearchTerm.toLowerCase();
        return companyName.toLowerCase().includes(searchTerm) || companyId.toLowerCase().includes(searchTerm);
    });

    const handleSelectBranch = (branch: Branch) => {
        setSelectedBranch(branch);
        setSelectedCompanyType(null);
        setSelectedCompany(null);
        setShowBranchModal(false);
        setBranchSearchTerm('');
    };

    const handleSelectCompanyType = (companyType: string) => {
        setSelectedCompanyType(companyType);
        setSelectedCompany(null);
    };

    const handleSelectCompany = (company: Company) => {
        setSelectedCompany(company);
        setShowCompanyModal(false);
        setCompanySearchTerm('');
    };

    const canSubmit = !!(
        selectedBranch &&
        selectedCompanyType &&
        selectedCompany &&
        requestDate &&
        deploymentDate &&
        reason.trim() &&
        requestorName.trim() &&
        requestorDesignation.trim() &&
        complaintStartDate &&
        (!resubmitRequest || resubmissionNote.trim())
    );

    const handlePreview = () => {
        if (!selectedBranch || !selectedCompanyType || !selectedCompany || !uid) {
            showToastMessage('Please select branch, company type, and new company', 'error');
            return;
        }
        if (!reason.trim()) {
            showToastMessage('Please enter reason / justification', 'error');
            return;
        }
        if (!requestorName.trim()) {
            showToastMessage('Please enter requestor name', 'error');
            return;
        }
        if (!requestorDesignation.trim()) {
            showToastMessage('Please enter requestor designation', 'error');
            return;
        }
        if (!complaintStartDate) {
            showToastMessage('Please enter complaint starting date', 'error');
            return;
        }
        if (resubmitRequest && !resubmissionNote.trim()) {
            showToastMessage('Please enter resubmission note / reason', 'error');
            return;
        }
        // Show preview modal
        setShowPreviewModal(true);
    };

    const handleSubmit = async (skipConflictCheck: boolean = true) => {
        if (!selectedBranch || !selectedCompanyType || !selectedCompany || !uid) {
            showToastMessage('Please select branch, company type, and new company', 'error');
            return;
        }

        // Validate mandatory fields
        if (!reason.trim()) {
            showToastMessage('Please enter reason / justification', 'error');
            return;
        }
        if (!requestorName.trim()) {
            showToastMessage('Please enter requestor name', 'error');
            return;
        }
        if (!requestorDesignation.trim()) {
            showToastMessage('Please enter requestor designation', 'error');
            return;
        }
        if (!complaintStartDate) {
            showToastMessage('Please enter complaint starting date', 'error');
            return;
        }
        if (resubmitRequest && !resubmissionNote.trim()) {
            showToastMessage('Please enter resubmission note / reason', 'error');
            return;
        }

        // Prevent double submission using ref
        if (isSubmittingRef.current || saving) {
            console.log('Already submitting, ignoring duplicate call');
            return;
        }

        isSubmittingRef.current = true;
        setSaving(true);
        // Don't close preview modal yet - keep it open to show loader
        
        try {
            console.log('Submitting change of vendor request...', {
                branchId: selectedBranch.id,
                companiesId: selectedCompany.id,
                companyType: selectedCompany.companyType,
                skipConflictCheck,
                isResubmit: !!resubmitRequest
            });

            // Convert date strings to Date objects
            const requestDateObj = requestDate ? new Date(requestDate) : new Date();
            const deploymentDateObj = deploymentDate ? new Date(deploymentDate) : undefined;
            
            if (resubmitRequest) {
                await submitResubmitChangeOfVendor(
                    uid,
                    isBankUser,
                    resubmitRequest.id,
                    resubmissionNote,
                    reason,
                    requestorName,
                    requestorDesignation,
                    complaintStartDate,
                    followUpDates
                );
                showToastMessage('✅ Change of vendor request resubmitted successfully!', 'success');
            } else {
                const result = await submitChangeOfVendor(
                    uid,
                    isBankUser,
                    selectedBranch.id,
                    selectedCompany.id,
                    true,
                    requestDateObj,
                    deploymentDateObj,
                    reason,
                    requestorName,
                    requestorDesignation,
                    complaintStartDate,
                    followUpDates
                );
                console.log('Submit result:', result);
                showToastMessage('✅ Change of vendor request submitted successfully!', 'success');
            }

            // Wait for save to complete, then show success and close
            // Small delay to ensure DB write is complete
            await new Promise(resolve => setTimeout(resolve, 500));
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error submitting change of vendor:', err);
            showToastMessage(`❌ Failed to submit request: ${err.message || 'Unknown error'}`, 'error');
            setSaving(false);
            isSubmittingRef.current = false;
        }
    };

    const handleConflictResolve = () => {
        setShowConflictModal(false);
        setConflictData(null);
    };

    const handleConflictSubmit = async () => {
        // Submit even with conflict (user confirmed)
        await handleSubmit(true);
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={resubmitRequest ? "Resubmit Change of Vendor Request" : "Request Change of Vendor"}
                size="lg"
            >
                <div className="space-y-6 px-6 max-h-[75vh] overflow-y-auto">
                    {/* Branch Selection */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Select Branch *
                        </label>
                        <div className="flex gap-2">
                            <TextField
                                type="text"
                                placeholder={selectedBranch ? `${selectedBranch.branchCode} - ${selectedBranch.address}` : "Click to select branch"}
                                value=""
                                readOnly
                                className="bg-gray-50 dark:bg-gray-800 flex-1"
                            />
                            <Button
                                variant="secondary"
                                onClick={() => setShowBranchModal(true)}
                                disabled={saving}
                            >
                                Select
                            </Button>
                        </div>
                    </div>

                    {/* Deployed Companies Display */}
                    {selectedBranch && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Currently Deployed Companies
                            </label>
                            {loadingDeployments ? (
                                <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                    Loading deployed companies...
                                </div>
                            ) : deployedCompanies.length === 0 ? (
                                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                        No companies are currently deployed to this branch.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {['CCTV', 'BAMS', 'Guard Company'].map((type) => {
                                        const companiesOfType = deployedCompanies.filter(d => d.companyType === type);
                                        if (companiesOfType.length === 0) return null;
                                        
                                        // Check if there's any existing request for this type (any status)
                                        const existingRequest = companiesOfType.find(d => d.existingRequestId);
                                        const hasPendingRequest = companiesOfType.some(d => d.hasPendingRequest);
                                        const existingRequestId = existingRequest?.existingRequestId;
                                        const existingRequestDocId = existingRequest?.existingRequestDocId;
                                        const existingRequestStatus = existingRequest?.existingRequestStatus;
                                        
                                        return (
                                            <div key={type} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/50">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            <span className="font-semibold text-gray-900 dark:text-gray-100">{type}:</span>
                                                            {existingRequestId && (
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                                                        Already Requested: 
                                                                    </span>
                                                                    {hasPendingRequest && existingRequestDocId ? (
                                                                        <button
                                                                            onClick={() => handleCancelRequest(existingRequestDocId, existingRequestId)}
                                                                            className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 cursor-pointer underline transition-colors"
                                                                            disabled={loadingDeployments}
                                                                        >
                                                                            {existingRequestId}
                                                                        </button>
                                                                    ) : (
                                                                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                                                                            {existingRequestId}
                                                                        </span>
                                                                    )}
                                                                    {existingRequestStatus && (
                                                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                                                            existingRequestStatus === 'Approved' 
                                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                                                : existingRequestStatus === 'Rejected'
                                                                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                                                                        }`}>
                                                                            {existingRequestStatus}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {hasPendingRequest && (
                                                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 italic">
                                                                Note: Cancel this id to create new request.
                                                            </p>
                                                        )}
                                                        {companiesOfType.map((deployed, idx) => (
                                                            <span key={deployed.deploymentId} className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                                                                {deployed.company.companyName}
                                                                {idx < companiesOfType.length - 1 && ', '}
                                                            </span>
                                                        ))}
                                                    </div>
                                                    {!hasPendingRequest && (
                                                        <Button
                                                            variant="secondary"
                                                            onClick={() => handleSelectCompanyType(type)}
                                                            className="px-3 py-1 text-xs whitespace-nowrap"
                                                            disabled={selectedCompanyType === type}
                                                        >
                                                            {selectedCompanyType === type ? 'Selected' : 'Change Vendor'}
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Company Type Selection Display */}
                    {selectedCompanyType && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Select New {selectedCompanyType} Company *
                            </label>
                            <div className="flex gap-2">
                                <TextField
                                    type="text"
                                    placeholder={selectedCompany ? selectedCompany.companyName : "Click to select company"}
                                    value=""
                                    readOnly
                                    className="bg-gray-50 dark:bg-gray-800 flex-1"
                                />
                                <Button
                                    variant="secondary"
                                    onClick={() => setShowCompanyModal(true)}
                                    disabled={loading}
                                >
                                    Select
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Requestor Name & Designation */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Requestor Name *
                            </label>
                            <TextField
                                type="text"
                                placeholder="Enter name"
                                value={requestorName}
                                onChange={(e) => setRequestorName(e.target.value)}
                                disabled={saving}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Requestor Designation *
                            </label>
                            <TextField
                                type="text"
                                placeholder="Enter designation"
                                value={requestorDesignation}
                                onChange={(e) => setRequestorDesignation(e.target.value)}
                                disabled={saving}
                            />
                        </div>
                    </div>

                    {/* Complaint Starting Date & Date of Deployment */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Complaint Starting Date (Email Corresponding Date) *
                            </label>
                            <TextField
                                type="date"
                                value={complaintStartDate}
                                onChange={(e) => setComplaintStartDate(e.target.value)}
                                disabled={saving}
                                max={requestDate}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Date of Deployment *
                            </label>
                            <TextField
                                type="date"
                                value={deploymentDate}
                                onChange={(e) => setDeploymentDate(e.target.value)}
                                min={requestDate}
                                disabled={!requestDate || saving}
                            />
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                Estimated deployment date (minimum 7 days from request date)
                            </p>
                        </div>
                    </div>

                    {/* Request Date (auto-generated) */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Request Date
                        </label>
                        <TextField
                            type="date"
                            value={requestDate}
                            disabled={true}
                            className="bg-gray-50 dark:bg-gray-800"
                        />
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Auto-generated (today's date)
                        </p>
                    </div>

                    {/* Reason / Justification */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Reason / Justification *
                        </label>
                        <MarkdownEditor
                            value={reason}
                            onChange={setReason}
                            placeholder="Enter reasons / justification in markdown..."
                            rows={4}
                            className="w-full"
                        />
                    </div>

                    {/* Branch / PSO Follow-up Dates */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Branch / PSO Follow-up Dates
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2 mb-2">
                            <TextField
                                type="date"
                                value={newFollowUpDate}
                                onChange={(e) => setNewFollowUpDate(e.target.value)}
                                disabled={saving}
                                className="flex-1"
                            />
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    if (!newFollowUpDate) return;
                                    if (followUpDates.includes(newFollowUpDate)) {
                                        showToastMessage('Date already added', 'warning');
                                        return;
                                    }
                                    setFollowUpDates([...followUpDates, newFollowUpDate].sort());
                                    setNewFollowUpDate('');
                                }}
                                className="w-full sm:w-auto whitespace-nowrap"
                                disabled={saving || !newFollowUpDate}
                            >
                                Add Date
                            </Button>
                        </div>
                        {followUpDates.length > 0 ? (
                            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/30">
                                {followUpDates.map((date) => (
                                    <span
                                        key={date}
                                        className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                                    >
                                        {new Date(date).toLocaleDateString()}
                                        <button
                                            type="button"
                                            onClick={() => setFollowUpDates(followUpDates.filter((d) => d !== date))}
                                            className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-200 focus:outline-none"
                                            disabled={saving}
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 italic">No follow-up dates added yet.</p>
                        )}
                    </div>

                    {/* Resubmission Note */}
                    {resubmitRequest && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                Resubmission Note / Reason *
                            </label>
                            <TextField
                                type="text"
                                placeholder="Enter resubmission reason"
                                value={resubmissionNote}
                                onChange={(e) => setResubmissionNote(e.target.value)}
                                disabled={saving}
                            />
                        </div>
                    )}

                    {/* Selected Details Summary */}


                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePreview();
                            }}
                            disabled={!canSubmit || saving}
                        >
                            Preview & Submit
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Branch Selection Modal */}
            <Modal
                isOpen={showBranchModal}
                onClose={() => setShowBranchModal(false)}
                title="Select Branch"
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by address or branch code..."
                            value={branchSearchTerm}
                            onChange={(e) => setBranchSearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Branch Code
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Address
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBranches.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No branches found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredBranches.map((branch) => (
                                        <tr key={branch.id} className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
                                            <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {branch.branchCode}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {branch.address}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Button
                                                    onClick={() => handleSelectBranch(branch)}
                                                    className="px-3 py-1.5 text-xs"
                                                >
                                                    Select
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* Company Selection Modal */}
            <Modal
                isOpen={showCompanyModal}
                onClose={() => setShowCompanyModal(false)}
                title={`Select New ${selectedCompanyType || ''} Company`}
                size="xl"
            >
                <div className="space-y-4 px-6">
                    <div className="relative">
                        <Icon icon={FiSearch} className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by company name or ID..."
                            value={companySearchTerm}
                            onChange={(e) => setCompanySearchTerm(e.target.value)}
                            className="w-full rounded-xl border border-gray-300 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:border-gray-600 dark:bg-black/30 dark:text-gray-100"
                        />
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-300 bg-gray-50 dark:bg-gray-800">
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Company Name
                                    </th>
                                    <th className="border-r border-gray-200 px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:border-gray-600 dark:text-gray-300">
                                        Type
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Company ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCompanies.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                                            No companies found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCompanies.map((company) => (
                                        <tr key={company.id} className="border-b border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800/50">
                                            <td className="border-r border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100" style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                                                <strong>{company.companyName}</strong>
                                            </td>
                                            <td className="border-r border-gray-200 px-4 py-3 dark:border-gray-700">
                                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                                    {company.companyType || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                                {company.companyId || 'N/A'}
                                            </td>
                                            <td className="px-4 py-3">
                                                <Button
                                                    onClick={() => handleSelectCompany(company)}
                                                    className="px-3 py-1.5 text-xs"
                                                >
                                                    Select
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>

            {/* Preview/Confirmation Modal */}
            <Modal
                isOpen={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
                title="Confirm Change of Vendor Request"
                size="lg"
            >
                <div className="space-y-4 px-6">
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
                        <h6 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">Request Preview</h6>
                        
                        {/* Branch Information */}
                        {selectedBranch && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Branch:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    <div><strong>Branch Code:</strong> {selectedBranch.branchCode}</div>
                                    <div><strong>Address:</strong> {selectedBranch.address}</div>
                                </div>
                            </div>
                        )}

                        {/* Company Type to Change */}
                        {selectedCompanyType && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Company Type to Change:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    {selectedCompanyType}
                                </div>
                            </div>
                        )}

                        {/* Current Deployed Company */}
                        {selectedCompanyType && deployedCompanies.length > 0 && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Current Vendor:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    {deployedCompanies
                                        .filter(d => d.companyType === selectedCompanyType)
                                        .map(d => d.company.companyName)
                                        .join(', ') || 'None'}
                                </div>
                            </div>
                        )}

                        {/* New Company */}
                        {selectedCompany && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">New Vendor:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    <div><strong>Company Name:</strong> {selectedCompany.companyName}</div>
                                    <div><strong>Company Type:</strong> {selectedCompany.companyType}</div>
                                    {selectedCompany.companyId && (
                                        <div><strong>Company ID:</strong> {selectedCompany.companyId}</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Requestor Info */}
                        <div className="mb-3">
                            <strong className="text-sm text-blue-800 dark:text-blue-200">Requestor:</strong>
                            <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                {requestorName} ({requestorDesignation})
                            </div>
                        </div>

                        {/* Complaint Starting Date */}
                        {complaintStartDate && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Complaint Starting Date:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    {new Date(complaintStartDate).toLocaleDateString()}
                                </div>
                            </div>
                        )}

                        {/* Reason / Justification */}
                        <div className="mb-3">
                            <strong className="text-sm text-blue-800 dark:text-blue-200">Justification:</strong>
                            <div className="mt-1 text-sm bg-white dark:bg-gray-900 p-3 rounded-lg border border-blue-100 dark:border-blue-800 max-h-40 overflow-y-auto">
                                <Markdown content={reason} />
                            </div>
                        </div>

                        {/* Follow-up Dates */}
                        {followUpDates.length > 0 && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Follow-up Dates:</strong>
                                <div className="mt-1 flex flex-wrap gap-1">
                                    {followUpDates.map(d => (
                                        <span key={d} className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                            {new Date(d).toLocaleDateString()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Resubmission Note */}
                        {resubmitRequest && resubmissionNote && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Resubmission Note:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    {resubmissionNote}
                                </div>
                            </div>
                        )}

                        {/* Request Date */}
                        {requestDate && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Request Date:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    {new Date(requestDate).toLocaleDateString()}
                                </div>
                            </div>
                        )}

                        {/* Deployment Date */}
                        {deploymentDate && (
                            <div className="mb-3">
                                <strong className="text-sm text-blue-800 dark:text-blue-200">Date of Deployment:</strong>
                                <div className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                                    {new Date(deploymentDate).toLocaleDateString()}
                                </div>
                            </div>
                        )}

                        {/* Status */}
                        <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700">
                            <strong className="text-sm text-blue-800 dark:text-blue-200">Status:</strong>
                            <div className="mt-1">
                                <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                    Pending
                                </span>
                            </div>
                        </div>
                    </div>

                    {saving && (
                        <div className="flex items-center justify-center py-4">
                            <div className="text-center">
                                <div className="mb-2 inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Saving to database...</p>
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={() => setShowPreviewModal(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSubmit(true);
                            }}
                            disabled={saving}
                        >
                            {saving ? 'Submitting...' : 'Confirm & Submit'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Conflict Resolution Modal */}
            <Modal
                isOpen={showConflictModal}
                onClose={handleConflictResolve}
                title="Change of Vendor Conflict"
                size="md"
            >
                <div className="space-y-4 px-6">
                    <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">
                        <div className="flex items-center gap-2">
                            <Icon icon={FiAlertTriangle} className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                            <strong className="text-yellow-900 dark:text-yellow-100">Warning:</strong>
                        </div>
                        <p className="mt-2 text-sm text-yellow-800 dark:text-yellow-200">
                            Branch already has a company of the same type. Your request will be submitted for approval.
                        </p>
                    </div>

                    <div>
                        <strong className="text-gray-900 dark:text-gray-100">Current Company:</strong>
                        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                            <strong>Company:</strong> {conflictData?.existingCompany?.companyName} ({conflictData?.existingCompany?.companyType})
                        </div>
                    </div>

                    <div>
                        <strong className="text-gray-900 dark:text-gray-100">Requested Company:</strong>
                        <div className="mt-1 ml-3 text-sm text-gray-600 dark:text-gray-400">
                            <strong>Company:</strong> {selectedCompany?.companyName} ({selectedCompany?.companyType})
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <Button variant="secondary" onClick={handleConflictResolve}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleConflictSubmit();
                            }}
                            disabled={saving}
                        >
                            {saving ? 'Submitting...' : 'Submit Request'}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Toast */}
            {showToast && (
                <div className="pointer-events-none fixed right-4 top-4 z-50">
                    <div className="pointer-events-auto">
                        <Toast
                            message={successMessage}
                            type={successMessage.includes('❌') ? 'error' : 'success'}
                            onClose={() => setShowToast(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default AddChangeOfVendorModal;

